import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Autocomplete } from "src/components/shared/MantineCombobox";
import { useInputProps } from "src/generator/hooks/useInputProps";
import { useSuspendAutosave } from "src/generator/hooks/useSuspendAutosave";
import { useGetApi, useIsOnline, useTheme } from "src/hooks";
import useSavedCursorPosition from "src/hooks/useSavedCursorPosition";
import useTriggerInlineSave from "src/hooks/useTriggerInlineSave";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setEditing } from "src/redux/slices/entrySlice";
import { getYachtId } from "src/utils";

import useGetSelectData from "../hooks/useGetSelectData";
import GhostTextOverlay from "./GhostTextOverlay";

interface Props {
  id: string;
  value: any;
  required?: boolean;
  onChange?: (value: string) => void;
  width?: string;
  sectionIndex?: number;
  tag?: string;
  rowIndex?: number;
  ambGetPreviousData?: string;
  searchEntries?: string;
  data?: any[];
  withLabel?: boolean;
  allowText?: boolean;
  colIndex?: number;
}

const mapper = (data: any) => {
  if (!data) return [];
  return [...new Set(data.list)];
};

const SelectAutocomplete = ({
  id,
  value,
  required = false,
  onChange,
  width,
  sectionIndex,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- omitted from the rest props on purpose
  tag,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- omitted from the rest props on purpose
  rowIndex,
  ambGetPreviousData,
  searchEntries,
  data = [],
  withLabel = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- omitted from the rest props on purpose
  allowText = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- omitted from the rest props on purpose
  colIndex,
  ...restProps
}: Props) => {
  const theme = useTheme();
  const isOnline = useIsOnline();
  const { logbookId } = useParams();
  const dispatch = useDispatch();
  const triggerSave = useTriggerInlineSave(sectionIndex);
  const { uppercase } = useSelector(state => state.preferencesSlice);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    onChange: defaultOnChange,
    ref: addToRefs,
    onKeyDown: handleLogbookKeyDown,
    ...inputProps
  } = useInputProps(id, value, required);
  const [suggestion, setSuggestion] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [shouldAutoFill, setShouldAutoFill] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [inputId] = useState(`mantine-input-${id}`);
  useSuspendAutosave(dropdownOpen);
  const getData = useGetSelectData({ data, value });

  const getCurrentValue = () => {
    if (!inputProps.value) return "";
    if (typeof inputProps.value === "string") return inputProps.value;
    if (typeof inputProps.value === "object" && inputProps.value?.value) {
      return inputProps.value.value;
    }
    return "";
  };

  const currentValue = getCurrentValue();

  const fetchSuggestions = async (val: string) => {
    if (!isOnline || !searchEntries) {
      setSuggestion("");
      return;
    }

    if (!val.trim()) {
      setSuggestion("");
      return;
    }

    const data = await refetch(
      {
        params: {
          text: val,
          searchID: searchEntries,
          lbid: logbookId,
          yachtid: getYachtId(),
        },
      },
      true,
    );

    if (!data || !Array.isArray(data) || data.length === 0) {
      setSuggestion("");
      return;
    }

    const match = data.find((word: string) =>
      word.toLowerCase().startsWith(val.toLowerCase()),
    );

    if (match && match.toLowerCase() !== val.toLowerCase()) {
      setSuggestion(match);
    } else {
      setSuggestion("");
    }
  };
  const handleChange = (val: string) => {
    // Special handling for security level - only allow 1, 2, or 3
    if (ambGetPreviousData === "seclevel") {
      if (val && !["1", "2", "3"].includes(val.trim())) {
        return; // Ignore invalid input for security level
      }
      // Map numeric input to full object format using existing data
      if (["1", "2", "3"].includes(val.trim())) {
        const secLevelNumber = val.trim();
        const item = getData().find(i =>
          i.value.includes(`Security Level ${secLevelNumber}`),
        );
        if (item) {
          const result = {
            key: item.key || item.chrisKey,
            value: item.label || item.value,
          };
          defaultOnChange(result);
          onChange?.(result as any);
          dispatch(setEditing(true));
          triggerSave();
          return;
        }
      }
    }

    const item = getData().find(i => i.value === val);

    if (!item) defaultOnChange(val);
    else defaultOnChange({ key: item.chrisKey, value: item.label });
    onChange?.(item as any);

    void fetchSuggestions(val);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Navigate first: accepting a suggestion below preventDefaults
    handleLogbookKeyDown(e);

    if (e.key === "Tab" && suggestion) {
      e.preventDefault();

      defaultOnChange(suggestion);
      onChange?.(suggestion);
      dispatch(setEditing(true));
      triggerSave();

      setSuggestion("");
    }
  };
  const handleItemSelect = (value: string) => {
    // Special handling for security level dropdown selection
    if (ambGetPreviousData === "seclevel") {
      const item = getData().find(i => i.label === value || i.value === value);
      if (item) {
        defaultOnChange({ key: item.key || item.chrisKey, value: item.label });
        onChange?.(item as any);
        dispatch(setEditing(true));
        triggerSave();
        setSuggestion("");
        return;
      }
    }

    const item = getData().find(i => i.label === value);

    defaultOnChange({ key: item.chrisKey, value: item.label });
    onChange?.(item as any);
    dispatch(setEditing(true));
    triggerSave();
    setSuggestion("");
  };

  const { refetch } = useGetApi({
    request: "search-entry-values",
    body: {
      params: {
        text: "",
        searchID: searchEntries,
        lbid: logbookId,
        yachtid: getYachtId(),
      },
    },
    fetchOnInit: false,
    mapper,
  });
  useEffect(() => {
    if (searchEntries && currentValue) {
      void fetchSuggestions(currentValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
  }, [searchEntries]); // Auto-fill security level with first item when field loses focus
  useEffect(() => {
    if (
      shouldAutoFill &&
      ambGetPreviousData === "seclevel" &&
      currentValue.trim()
    ) {
      // Get the filtered data that's currently being displayed in the dropdown
      const filteredData =
        data?.filter(el =>
          el.label.toLowerCase().includes(currentValue.toLowerCase().trim()),
        ) || [];

      // Use the first item from the filtered results, or fallback to first item from all data
      const firstItem = filteredData[0] || getData()[0];

      if (firstItem) {
        const result = {
          key: firstItem.key || firstItem.chrisKey,
          value: firstItem.label || firstItem.value,
        };
        defaultOnChange(result);
        onChange?.(result as any);
        dispatch(setEditing(true));
        triggerSave();
      }
      setShouldAutoFill(false);
    }
  }, [
    shouldAutoFill,
    ambGetPreviousData,
    currentValue,
    data,
    getData,
    defaultOnChange,
    onChange,
    dispatch,
    triggerSave,
  ]);

  const { handleFocus, handleBlur } = useSavedCursorPosition(id, inputRef);
  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    handleFocus(e);
    setIsFocused(true);
    setShouldAutoFill(false); // Reset auto-fill when focusing

    setTimeout(() => {
      const inputElement = document
        .querySelector(`.${inputId}`)
        ?.querySelector("input");
      if (inputElement && inputRef.current !== inputElement) {
        (inputRef.current as any) = inputElement as HTMLInputElement;
      }
    }, 0);

    if (currentValue) {
      void fetchSuggestions(currentValue);
    }
  };
  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    handleBlur(e);
    setIsFocused(false);

    // Trigger auto-fill for security level if needed
    if (ambGetPreviousData === "seclevel") {
      setShouldAutoFill(true);
    }

    setTimeout(() => {
      setSuggestion("");
    }, 150);
  };

  const label = useMemo(() => {
    if (!withLabel) return "";
    const item = getData().find((item: any) => item.value === currentValue);
    return item?.inputLabel ?? "";
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
  }, [data, withLabel, currentValue]);
  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        backgroundColor: theme?.colors?.inputBackgroud?.[0] ?? "",
      }}>
      <Autocomplete
        {...restProps}
        id={id}
        ref={(el: HTMLInputElement | null) => {
          (inputRef as any).current = el;
          addToRefs(el);
        }}
        className={inputId}
        label={withLabel ? label : null}
        value={currentValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        onOptionSubmit={handleItemSelect}
        onDropdownOpen={() => setDropdownOpen(true)}
        onDropdownClose={() => setDropdownOpen(false)}
        data={data?.map(el => el.label)}
        limit={20}
        miw={width ? Number(width) * 10 - 20 : 200}
        filter={({ options, search }) => {
          const searchLower = search.toLowerCase().trim();
          if (!searchLower) return options;
          return options.filter(option => {
            if (!("value" in option)) return true;
            const value = String(option.value ?? "").toLowerCase();
            return value.includes(searchLower);
          });
        }}
        inputWrapperOrder={["label", "input", "error", "description"]}
        comboboxProps={{
          withinPortal: true,
          portalProps: { target: document.body },
        }}
        styles={{
          input: {
            textTransform: uppercase ? "uppercase" : "none",
            position: "relative",
            backgroundColor: "transparent",
            zIndex: 2,
          },
          dropdown: {
            zIndex: 1000,
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            maxHeight: "220px",
            overflow: "hidden",
          },
          root: {
            position: "relative",
          },
          item: {
            padding: "8px 12px",
          },
        }}
        zIndex={2000}
      />

      <GhostTextOverlay
        targetInputId={inputId}
        suggestion={suggestion}
        value={currentValue}
        uppercase={uppercase}
        visible={isFocused}
      />
    </div>
  );
};

export default SelectAutocomplete;
